<script lang="ts">
	import PageHeader from '$lib/components/PageHeader.svelte';
	import Pill from '$lib/components/Pill.svelte';
	import LiveTimer from '$lib/components/LiveTimer.svelte';
	import { Brain, Split, Compass, StickyNote, Tags, ArrowLeft, ArrowRight } from '@lucide/svelte';

	// A snapshot of an afternoon: several loops in flight at once. Notes are the
	// breadcrumbs; one loop is the current focus, the rest are parked.
	const loops = [
		{
			tone: 'red' as const,
			labels: [
				['red', 'repo: sfi/sfi-website'],
				['blue', 'feat: kb-knowledge-graph']
			],
			note: 'graph query returns dupes — check the join in loader.ts',
			start: 432, // 7:12
			running: true
		},
		{
			tone: 'green' as const,
			labels: [
				['green', 'repo: sfi/symbology'],
				['amber', 'area: faceted-browse']
			],
			note: 'agent drafting the migration — review schema before applying',
			start: 243, // 4:03
			running: false
		},
		{
			tone: 'blue' as const,
			labels: [
				['blue', 'repo: dial-in'],
				['red', 'fix: sync-retry']
			],
			note: 'reproduced the bug; prompt agent for a root-cause next',
			start: 108, // 1:48
			running: false
		}
	] as const;

	const hurdles = [
		{
			icon: Brain,
			title: 'Cognitive overload',
			body: 'Holding many live threads in your head at once is hard — and it gets harder the moment one of them is interrupted. The details you needed are the first thing to evaporate.'
		},
		{
			icon: Split,
			title: 'Context-switching tax',
			body: 'Jumping between two projects is manageable. Past two, the reload cost compounds: every switch means rebuilding where you were, what you were doing, and why.'
		}
	];

	const outcomes = [
		{
			stat: '6 loops',
			label: 'In flight, one plan',
			body: 'Several agent loops across projects, tracked side by side without dropping the thread that ties them together.'
		},
		{
			stat: '< 10s',
			label: 'To re-orient',
			body: 'A glance at the label and the last note is usually all it takes to pick a parked loop back up.'
		},
		{
			stat: '0 threads',
			label: 'Lost to a distraction',
			body: 'Interruptions leave a breadcrumb instead of a black hole; nothing has to be reconstructed from memory.'
		}
	];
</script>

<svelte:head>
	<title>Staying coherent across parallel loops — Moment Tally case study</title>
	<meta
		name="description"
		content="A new development paradigm: managing several agent loops across multiple projects in parallel. How StreetFortress uses Moment Tally's labels and notes as breadcrumbs to beat cognitive overload and context-switching costs."
	/>
</svelte:head>

<PageHeader
	eyebrow="Case study · The parallel paradigm"
	title="Staying coherent across parallel loops"
	lead="A new way of working is here: driving several agent loops, in multiple projects, all at once — while trying to stay on course. The hardest part about levelling up isn't the faster pace; it's the compounding complexity that comes with it. Moment Tally leaves a trail of breadcrumbs so you never lose the thread."
/>

<div class="mx-auto max-w-4xl px-5 py-16">
	<a
		href="/case-study"
		class="inline-flex items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-accent"
	>
		<ArrowLeft size={15} /> All case studies
	</a>

	<!-- The challenge -->
	<section class="mt-10">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">The challenge</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">
			Beyond a single session, the wheels start to wobble
		</h2>
		<p class="mt-4 leading-relaxed text-fg-muted">
			One agent, one project, one session is easy to hold in your head. The step change — and the
			real productivity — comes from running <span class="text-fg">many loops in parallel</span>.
			But that's exactly where most developers stall out, because two forces work against you at
			once:
		</p>
		<div class="mt-6 grid gap-4 sm:grid-cols-2">
			{#each hurdles as h (h.title)}
				<div class="card p-6">
					<div
						class="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface/60 text-accent"
					>
						<h.icon size={20} />
					</div>
					<h3 class="font-display text-lg font-bold">{h.title}</h3>
					<p class="mt-2 text-sm leading-relaxed text-fg-muted">{h.body}</p>
				</div>
			{/each}
		</div>
	</section>

	<!-- The board: parallel loops -->
	<section class="mt-14">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">A snapshot</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">
			What an afternoon actually looks like
		</h2>
		<p class="mt-4 leading-relaxed text-fg-muted">
			Three loops in flight — one you're on right now, two parked mid-thought. Each carries its own
			labels and its own last note, so a parked loop is never a cold start.
		</p>

		<div class="mt-6 grid gap-4 md:grid-cols-3">
			{#each loops as loop, i (i)}
				<div
					class="card p-5 {loop.running ? 'ring-1 ring-accent/40' : ''}"
					aria-label={loop.running ? 'current focus' : 'parked loop'}
				>
					<div class="flex items-center justify-between">
						<LiveTimer
							start={loop.start}
							running={loop.running}
							class="font-mono text-sm font-semibold {loop.running
								? 'text-accent'
								: 'text-fg-faint'}"
						/>
						{#if loop.running}
							<span
								class="inline-flex items-center gap-1.5 font-mono text-[0.65rem] uppercase tracking-widest text-accent"
							>
								<span class="h-2 w-2 rounded-sm bg-accent"></span> live
							</span>
						{:else}
							<span
								class="inline-flex items-center gap-1.5 font-mono text-[0.65rem] uppercase tracking-widest text-fg-faint"
							>
								<span class="h-2 w-2 rounded-sm border border-border-strong"></span> parked
							</span>
						{/if}
					</div>
					<div class="mt-3 flex flex-wrap gap-1.5">
						{#each loop.labels as [tone, text] (text)}
							<Pill tone={tone as 'red' | 'green' | 'blue' | 'amber'}>{text}</Pill>
						{/each}
					</div>
					<div class="mt-3 flex gap-2 border-t border-border/70 pt-3">
						<StickyNote size={14} class="mt-0.5 shrink-0 text-fg-faint" />
						<p class="text-xs leading-relaxed text-fg-muted">{loop.note}</p>
					</div>
				</div>
			{/each}
		</div>
		<p class="mt-3 font-mono text-xs text-fg-faint">
			↑ notes are added on the fly — or after the fact — the instant you step away.
		</p>
	</section>

	<!-- The approach: breadcrumbs at two scales -->
	<section class="mt-16">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">The approach</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">
			Breadcrumbs at two scales
		</h2>
		<p class="mt-4 leading-relaxed text-fg-muted">
			Re-orienting is a two-part problem — where was I, and what exactly was I doing? Moment Tally
			answers each with a different tool.
		</p>

		<div class="mt-6 grid gap-4 md:grid-cols-2">
			<div class="card p-6">
				<div class="mb-4 inline-flex items-center gap-2 text-accent">
					<Tags size={18} />
					<span class="font-mono text-xs uppercase tracking-widest">Macro · labels</span>
				</div>
				<h3 class="font-display text-lg font-bold">Where was I?</h3>
				<p class="mt-2 text-sm leading-relaxed text-fg-muted">
					Span labels place you instantly: <span class="text-fg"
						>twenty minutes ago, before this distraction, I was in <span class="font-mono"
							>repo x</span
						>
						on
						<span class="font-mono">feature y</span></span
					>. The big picture, at a glance.
				</p>
				<div class="mt-4 flex flex-wrap gap-1.5">
					<Pill tone="red">repo: x</Pill>
					<Pill tone="blue">feat: y</Pill>
				</div>
			</div>

			<div class="card p-6">
				<div class="mb-4 inline-flex items-center gap-2 text-accent">
					<StickyNote size={18} />
					<span class="font-mono text-xs uppercase tracking-widest">Micro · notes</span>
				</div>
				<h3 class="font-display text-lg font-bold">What was I doing?</h3>
				<p class="mt-2 text-sm leading-relaxed text-fg-muted">
					Span notes fill in the detail. A quick
					<span class="text-fg">“working on this aspect of feature y — review CI”</span> gets you back
					on track almost instantly, no rebuild required.
				</p>
				<div class="mt-4 flex gap-2 rounded-lg border border-border bg-bg-raised/40 px-3 py-2">
					<StickyNote size={13} class="mt-0.5 shrink-0 text-fg-faint" />
					<p class="font-mono text-xs text-fg-muted">working on feat y — review CI, then retag</p>
				</div>
			</div>
		</div>

		<div class="mt-4 flex items-start gap-3 rounded-xl border border-border bg-bg-raised/30 p-5">
			<Compass size={20} class="mt-0.5 shrink-0 text-accent" />
			<p class="text-sm leading-relaxed text-fg-muted">
				Together they turn a context switch from a cliff into a step. The label tells you which
				thread; the note hands you back the exact place you left it.
			</p>
		</div>
	</section>

	<!-- The outcome -->
	<section class="mt-16">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">The outcome</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">
			More loops, still coherent
		</h2>
		<div class="mt-6 grid gap-4 sm:grid-cols-3">
			{#each outcomes as o (o.label)}
				<div class="card p-6">
					<p class="font-display text-3xl font-extrabold text-grad">{o.stat}</p>
					<p class="mt-2 font-semibold">{o.label}</p>
					<p class="mt-1 text-sm leading-relaxed text-fg-muted">{o.body}</p>
				</div>
			{/each}
		</div>
	</section>

	<!-- pull quote -->
	<blockquote class="card relative mt-12 overflow-hidden p-8 sm:p-10">
		<p class="font-display text-xl font-bold leading-snug sm:text-2xl">
			“The bottleneck stopped being how many agents I could run. It became how fast I could
			re-orient — and a label plus a one-line note
			<span class="text-grad">gets me there in seconds</span>.”
		</p>
		<footer class="mt-4 font-mono text-xs uppercase tracking-widest text-fg-faint">
			StreetFortress Industries · engineering
		</footer>
	</blockquote>

	<!-- CTA -->
	<section class="mt-12 text-center">
		<div class="flex flex-wrap justify-center gap-3">
			<a
				href="/docs#install"
				class="btn-accent rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-105"
			>
				Get Moment Tally
			</a>
			<a
				href="/case-study/joined-to-what-shipped"
				class="inline-flex items-center gap-1.5 rounded-full border border-border-strong px-6 py-3 text-sm font-medium text-fg-muted transition-colors hover:border-accent hover:text-fg"
			>
				Read: time, joined to what shipped <ArrowRight size={15} />
			</a>
		</div>
	</section>
</div>
